import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import { schema } from "../../misc/schema";

import { Container } from "./styles";
import {
	Inputs,
	stepsLabel,
	stepsStart,
	stepsLimit,
	initialValues,
	steps,
} from "./utils";
import { MyDynamicInput } from "../MyDynamicInput";
import { useWatch } from "react-hook-form";
import { useState } from "react";
import { useEffect } from "react";
import { cep_response } from "../../services/getLocations";
import { notify } from "../Toast";
import { StepFormTypes } from "./types";

export const StepForm = ({
	process,
	step = 0,
	handleFormSend,
}: StepFormTypes) => {
	const {
		handleSubmit,
		clearErrors,
		watch,
		setError,
		control,
		setValue,
		formState: { errors },
	} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(schema),
	});
	const [currentStep, setCurrentStep] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(
		step,
	);
	const [inputFields, setInputFields] = useState(steps[step]);

	const values = watch();

	useEffect(() => {
		if (values.valorRendaBruta && values.valorRendaLiquida) {
			let toIntBrute = parseInt(values.valorRendaBruta);
			let toIntLiquid = parseInt(values.valorRendaLiquida);

			if (toIntLiquid > toIntBrute) {
				notify({
					message: "A renda líquida não pode exceder a renda bruta",
					type: "error",
				});
				setValue("valorRendaLiquida", "");
			}
		}
	}, [values]);

	const cepValue = useWatch({ control, name: "cep" });

	useEffect(() => {
		if (process) {
			console.log("process", process);
			setValue("nomeCompleto", process.proponent_name);
			setValue("cpf", process.cpf);
		}
	}, [process]);

	useEffect(() => {
		setInputFields((state) => steps[currentStep]);
	}, [currentStep]);

	useEffect(() => {
		let isCancelled = false;

		const setAddress = async (cep: string) => {
			if (!cep) {
				return;
			}
			const cepNumbers = cep
				.split("")
				.filter(
					(n: string) => !isNaN(parseFloat(n)) && isFinite(Number(n)),
				)
				.join("");

			if (cepNumbers.length !== 8) {
				setError("cep", {
					type: "invalid format",
					message: "CEP inválido!",
				});
				return;
			}

			const cepData = await cep_response(cepNumbers);

			if (cepData.erro) {
				setError("cep", {
					type: "does not exists",
					message:
						"O CEP informado não é corresponde a nenhum endereço!",
				});
				return;
			}

			if (errors.cep) {
				clearErrors("cep");
			}

			const {
				uf,
				logradouro: street,
				localidade: city,
				bairro: district,
			} = cepData;

			if (!isCancelled) {
				setValue("cmbMunicipioEndereco", city);
				setValue("logradouro", street);
				setValue("bairro", district);
				setValue("cmbUFEndereco", uf);
			}
		};

		setAddress(cepValue);
		return () => {
			isCancelled = true;
		};
	}, [cepValue]);

	const onSubmit: SubmitHandler<Inputs> = (data) => handleFormSend(data);
	const onError: SubmitErrorHandler<Inputs> = (errors) => {
		console.log("errors: " + JSON.stringify(errors));
		// renderAlertAndConsole(errors);
		notify({
			message:
				"Formulário com erro: Verifique novamente os dados preenchidos",
			type: "error",
		});
	};

	return (
		<Container>
			<div className='header'>
				<h2>{`${currentStep + 1} - ${stepsLabel[currentStep]}`}</h2>
			</div>

			<form onSubmit={handleSubmit(onSubmit, onError)}>
				<div className='container-inputs'>
					{inputFields.map((inputField, index) => {
						const dependent = (
							inputField as {
								dependent:
									| {
											field: string;
											value?: string;
											values?: string[];
											valueFunction?: (
												dependentValue: string,
											) => boolean;
									  }
									| undefined;
							}
						).dependent;
						let dependentValue = "";
						let show = true;

						if (dependent) {
							dependentValue = values[dependent?.field];

							if (dependent.value) {
								//dependente com único valor

								show = dependentValue === dependent.value;
							}
							if (dependent.values) {
								//dependente com multiplos valores
								show = dependent.values.filter(
									(val) => val === dependentValue,
								)[0]
									? true
									: false;
							}

							if (dependent.valueFunction) {
								//dependente com valor mais complexo usa function
								show = dependent.valueFunction(dependentValue);
							}
						}

						if (!show && values[inputField.name]) {
							//Dois names se repetem no customOptios, tratando eles.
							if (
								inputField.name === "cmbOrgaoEmissorRg" ||
								inputField.name === "identidadeNumeroRg"
							) {
								//Sempre vai ter um orgao emissor, nao é necessário fazer uma verificação mais completa.
								if (inputField.name === "identidadeNumeroRg") {
									if (
										values["cmbTipoDocumento"] ===
											"0372102" ||
										values["cmbTipoDocumento"] === "0372008"
									) {
										//Vai fazer nada, pq um campo input deve aparecer mesmo
									} else {
										setValue(inputField.name, undefined);
									}
								}
							} else {
								setValue(inputField.name, undefined);
							}
							//Se não devia aparecer e está com valor, retira esse valor
						}

						return (
							show && (
								<MyDynamicInput
									key={`${inputField.name}`}
									setValue={setValue}
									data={inputField}
									control={control}
								/>
							)
						);
					})}
				</div>

				<div className='control-buttons'>
					{/* Botão Voltar*/}
					{currentStep > stepsStart ? (
						<button
							type='button'
							onClick={() =>
								setCurrentStep(
									(currentStep - 1) as
										| 0
										| 1
										| 2
										| 3
										| 4
										| 5
										| 6,
								)
							}
							className='btn-back'>
							Voltar
						</button>
					) : (
						""
					)}

					{/*Botão Próximo*/}
					{currentStep < stepsLimit ? (
						<button
							type='button'
							onClick={() =>
								setCurrentStep(
									(currentStep + 1) as
										| 0
										| 1
										| 2
										| 3
										| 4
										| 5
										| 6,
								)
							}
							className='btn-next'>
							Próxima Etapa
						</button>
					) : (
						""
					)}

					{/* Botão Enviar */}
					{currentStep === stepsLimit ? (
						<input
							className='btn-send'
							type='submit'
							placeholder='Enviar'
						/>
					) : (
						""
					)}
				</div>
			</form>
		</Container>
	);
};
